import { getStoredSessionToken } from "./appleAuth";
import { prepareCatalogUpload, type CatalogImageKind, type CatalogUploadOptions } from "./catalogImages";
import type {
  AccountType,
  CardProduct,
  CardType,
  CatalogMeta,
  CatalogSnapshot,
  Institution,
  InvestmentAsset,
  MerchantIcon,
  Network,
  SeedPayload,
} from "./catalogTypes";

async function authHeader(): Promise<HeadersInit> {
  const token = getStoredSessionToken();
  if (!token) throw new Error("Sign in required.");
  return { Authorization: `Bearer ${token}` };
}

async function catalogFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);
  if (!headers.has("Authorization")) {
    const auth = await authHeader();
    for (const [k, v] of Object.entries(auth)) headers.set(k, String(v));
  }
  return fetch(`/api/catalog/${path}`, { ...init, headers });
}

async function readJson<T>(res: Response): Promise<T> {
  const data = (await res.json()) as T & { error?: string };
  if (!res.ok) {
    throw new Error((data as { error?: string }).error ?? `Request failed (${res.status})`);
  }
  return data;
}

export async function fetchCatalogStatus(): Promise<{
  cloudKit: boolean;
  r2: boolean;
  appleAuth: boolean;
}> {
  const res = await fetch("/api/catalog/status");
  return readJson(res);
}

export async function fetchCatalogSnapshot(): Promise<CatalogSnapshot & { configured?: boolean }> {
  const res = await fetch("/api/catalog/snapshot");
  return readJson(res);
}

export async function publishCatalog(): Promise<CatalogMeta> {
  const res = await catalogFetch("publish", { method: "POST" });
  return readJson(res);
}

export type PromoteProductionMode = "new" | "all";

export type PromoteProductionResult = {
  mode: PromoteProductionMode;
  copied: Record<string, number>;
  unchanged: Record<string, number>;
  /** Subset of `copied` that updated an existing Production record name. */
  updated?: Record<string, number>;
  /** Subset of `copied` that created a record name missing in Production. */
  created?: Record<string, number>;
  skipped: Array<{ recordType: string; reason: string }>;
  total: number;
};

export type ProductionPendingResult = {
  /** New + edited rows still missing from / behind Production. */
  pending: Record<string, number>;
  /** Development record names absent from Production. */
  pendingNew?: Record<string, number>;
  /** Shared record names whose catalog fields differ. */
  pendingChanged?: Record<string, number>;
  inProduction: Record<string, number>;
  inDevelopment: Record<string, number>;
  total: number;
  totalNew?: number;
  totalChanged?: number;
};

export async function fetchProductionPendingCatalog(): Promise<ProductionPendingResult> {
  const res = await catalogFetch("promote-production/pending");
  return readJson(res);
}

export async function promotePublicCatalogToProduction(
  mode: PromoteProductionMode = "new",
): Promise<PromoteProductionResult> {
  const res = await catalogFetch("promote-production", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ mode }),
  });
  return readJson(res);
}

export const SEED_COLLECTIONS = [
  "institutions",
  "networks",
  "cardTypes",
  "accountTypes",
  "products",
  "merchantIcons",
  "investmentAssets",
] as const;

export type SeedCollection = (typeof SEED_COLLECTIONS)[number];

export const SEED_COLLECTION_LABELS: Record<SeedCollection, string> = {
  institutions: "Institutions",
  networks: "Networks",
  cardTypes: "Card types",
  accountTypes: "Account types",
  products: "Cards",
  merchantIcons: "Merchants",
  investmentAssets: "Investment assets",
};

type SeedResult = {
  written: number;
  collections: Record<string, number>;
  published: boolean;
};

/**
 * Applies one slice of the seed. Each collection is sent as its own request so a large
 * seed never exceeds the Worker subrequest budget for a single invocation.
 */
export async function seedCatalog(
  payload: SeedPayload,
  options?: { collections?: SeedCollection[]; publish?: boolean },
): Promise<SeedResult> {
  const res = await catalogFetch("seed", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...payload,
      collections: options?.collections,
      publish: options?.publish !== false,
    }),
  });
  return readJson(res);
}

/** Applies the requested collections one request at a time, publishing once at the end. */
export async function seedCatalogCollections(
  payload: SeedPayload,
  collections: readonly SeedCollection[],
): Promise<{ written: number }> {
  let written = 0;
  for (const [index, collection] of collections.entries()) {
    const result = await seedCatalog(payload, {
      collections: [collection],
      publish: index === collections.length - 1,
    });
    written += result.written;
  }
  return { written };
}

const MERCHANT_SEED_BATCH = 150;

/**
 * Writes MerchantIcon rows in CloudKit-sized batches without bumping contentVersion
 * until the caller publishes. Used by brand import so a large NSI slice stays inside
 * the Worker subrequest budget.
 */
export async function seedMerchantIcons(
  merchants: NonNullable<SeedPayload["merchantIcons"]>,
  options?: { publish?: boolean },
): Promise<{ written: number }> {
  let written = 0;
  for (let i = 0; i < merchants.length; i += MERCHANT_SEED_BATCH) {
    const chunk = merchants.slice(i, i + MERCHANT_SEED_BATCH);
    const result = await seedCatalog(
      {
        schemaVersion: 10,
        institutions: [],
        networks: [],
        cardTypes: [],
        accountTypes: [],
        products: [],
        merchantIcons: chunk,
      },
      { collections: ["merchantIcons"], publish: false },
    );
    written += result.written;
  }
  if (options?.publish && written > 0) {
    await publishCatalog();
  }
  return { written };
}

export async function normalizeSortOrders(): Promise<{
  updated: number;
  collections: Record<string, number>;
}> {
  const res = await catalogFetch("normalize-sort-orders", { method: "POST" });
  return readJson(res);
}

/**
 * Uploads catalog artwork after converting it to what the app can draw: vector input is
 * rasterized (the app cannot decode SVG) and oversized bitmaps are scaled to the size
 * the app renders, so devices don't download pixels they never use.
 */
export async function uploadCatalogImage(
  file: File,
  kind: CatalogImageKind,
  options?: CatalogUploadOptions,
): Promise<string> {
  const prepared = await prepareCatalogUpload(file, kind, options);
  const form = new FormData();
  form.set("file", prepared);
  form.set("kind", kind);
  const res = await catalogFetch("upload", { method: "POST", body: form });
  const data = await readJson<{ publicUrl: string }>(res);
  return data.publicUrl;
}

/**
 * Re-publishes existing R2 vector art as an app-compatible raster file.
 * The authenticated same-origin read avoids depending on the bucket's browser CORS policy.
 */
export async function convertPublishedCatalogImage(
  sourceURL: string,
  kind: CatalogImageKind,
  options?: CatalogUploadOptions,
): Promise<string> {
  const source = await catalogFetch(`artwork-source?url=${encodeURIComponent(sourceURL)}`);
  if (!source.ok) {
    await readJson(source);
  }
  const blob = await source.blob();
  const filename = new URL(sourceURL).pathname.split("/").pop() || "artwork.svg";
  const file = new File([blob], filename, {
    type: source.headers.get("Content-Type")?.split(";")[0] || "image/svg+xml",
  });
  return uploadCatalogImage(file, kind, options);
}

export async function importAppStoreArtwork(input: {
  appIDOrURL: string;
  countryCode: string;
  artworkURL?: string;
  appStoreURL?: string;
  appName?: string;
}): Promise<{
  appStoreAppID: string;
  appStoreURL: string;
  logoURL: string;
  appName: string;
}> {
  const res = await catalogFetch("app-store-artwork", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson(res);
}

export async function saveInstitution(
  input: Omit<Institution, "recordName"> & { recordName?: string },
): Promise<Institution> {
  const res = await catalogFetch("institutions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson(res);
}

export async function deleteInstitution(recordName: string): Promise<void> {
  const res = await catalogFetch(`institutions/${encodeURIComponent(recordName)}`, {
    method: "DELETE",
  });
  await readJson(res);
}

export async function saveNetwork(
  input: Omit<Network, "recordName"> & { recordName?: string },
): Promise<Network> {
  const res = await catalogFetch("networks", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson(res);
}

export async function deleteNetwork(recordName: string): Promise<void> {
  const res = await catalogFetch(`networks/${encodeURIComponent(recordName)}`, { method: "DELETE" });
  await readJson(res);
}

export async function saveCardType(
  input: Omit<CardType, "recordName"> & { recordName?: string },
): Promise<CardType> {
  const res = await catalogFetch("card-types", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson(res);
}

export async function deleteCardType(recordName: string): Promise<void> {
  const res = await catalogFetch(`card-types/${encodeURIComponent(recordName)}`, {
    method: "DELETE",
  });
  await readJson(res);
}

export async function saveAccountType(
  input: Omit<AccountType, "recordName" | "eligibleInstitutionKindsJSON"> & {
    recordName?: string;
    allowedCurrencyCodes?: string[] | null;
    eligibleInstitutionKindsJSON?: string | null;
    eligibleInstitutionKinds?: string[] | null;
  },
): Promise<AccountType> {
  const res = await catalogFetch("account-types", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson(res);
}

export async function deleteAccountType(recordName: string): Promise<void> {
  const res = await catalogFetch(`account-types/${encodeURIComponent(recordName)}`, {
    method: "DELETE",
  });
  await readJson(res);
}

export async function saveProduct(
  input: Partial<CardProduct> & { productName: string },
): Promise<CardProduct> {
  const res = await catalogFetch("products", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson(res);
}

export async function deleteProduct(recordName: string): Promise<void> {
  const res = await catalogFetch(`products/${encodeURIComponent(recordName)}`, {
    method: "DELETE",
  });
  await readJson(res);
}

export async function saveMerchantIcon(
  input: Omit<
    MerchantIcon,
    | "recordName"
    | "walletCategory"
    | "granularKind"
    | "countryScopeJSON"
    | "wikidataId"
    | "nsiId"
    | "sourceKind"
    | "mergedIntoRecordName"
  > & {
    recordName?: string;
    matchTerms?: string[];
    walletCategory?: MerchantIcon["walletCategory"] | string | null;
    granularKind?: string | null;
    countryScopeJSON?: string | null;
    countryScope?: string[] | null;
    wikidataId?: string | null;
    nsiId?: string | null;
    sourceKind?: MerchantIcon["sourceKind"] | string | null;
    mergedIntoRecordName?: string | null;
  },
): Promise<MerchantIcon> {
  const res = await catalogFetch("merchant-icons", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson(res);
}

export async function deleteMerchantIcon(recordName: string): Promise<void> {
  const res = await catalogFetch(`merchant-icons/${encodeURIComponent(recordName)}`, {
    method: "DELETE",
  });
  await readJson(res);
}

export async function saveInvestmentAsset(
  input: Omit<InvestmentAsset, "recordName"> & { recordName?: string },
): Promise<InvestmentAsset> {
  const res = await catalogFetch("investment-assets", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  return readJson(res);
}

export async function deleteInvestmentAsset(recordName: string): Promise<void> {
  const res = await catalogFetch(`investment-assets/${encodeURIComponent(recordName)}`, {
    method: "DELETE",
  });
  await readJson(res);
}
